const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

function getToken() {
  return localStorage.getItem('token');
}

export async function login(username, password) {
  const res = await fetch(`${API_BASE}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || 'Login failed');
  return data;
}

export async function forgotPassword(email) {
  const res = await fetch(`${API_BASE}/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || 'Request failed');
  return data;
}

export async function resetPassword(token, email, password, passwordConfirmation) {
  const res = await fetch(`${API_BASE}/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      token,
      email,
      password,
      password_confirmation: passwordConfirmation,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data.errors?.password?.[0]
      || data.errors?.email?.[0]
      || data.message
      || 'Failed to reset password';
    throw new Error(msg);
  }
  return data;
}

export async function logout() {
  const token = getToken();
  if (token) {
    await fetch(`${API_BASE}/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  }
}

export async function getMe() {
  const res = await authFetch(`${API_BASE}/me`);
  if (!res.ok) throw new Error('Failed to fetch user profile');
  return res.json();
}

export async function changePassword(currentPassword, newPassword, newPasswordConfirmation) {
  const res = await authFetch(`${API_BASE}/me/password`, {
    method: 'PUT',
    body: JSON.stringify({
      current_password: currentPassword,
      password: newPassword,
      password_confirmation: newPasswordConfirmation,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data.errors?.current_password?.[0]
      || data.errors?.password?.[0]
      || data.message
      || 'Failed to change password';
    throw new Error(msg);
  }
  return data;
}

export async function getUsers() {
  const res = await authFetch(`${API_BASE}/users`);
  if (!res.ok) throw new Error('Failed to fetch users');
  return res.json();
}

export async function createUser(data) {
  const res = await authFetch(`${API_BASE}/users`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to create user');
  }
  return res.json();
}

export async function updateUser(id, data) {
  const res = await authFetch(`${API_BASE}/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to update user');
  }
  return res.json();
}

export async function deleteUser(id) {
  const res = await authFetch(`${API_BASE}/users/${id}`, { method: 'DELETE' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to delete user');
  }
}

export async function getGroups({ sortBy, sortOrder } = {}) {
  const params = new URLSearchParams();
  if (sortBy) params.set('sortBy', sortBy);
  if (sortOrder) params.set('sortOrder', sortOrder);
  const res = await authFetch(`${API_BASE}/groups${params.toString() ? `?${params}` : ''}`);
  if (!res.ok) throw new Error('Failed to fetch groups');
  return res.json();
}

export async function getGroup(id) {
  const res = await authFetch(`${API_BASE}/groups/${id}`);
  if (!res.ok) throw new Error('Failed to fetch group');
  return res.json();
}

export async function createGroup(data) {
  const res = await authFetch(`${API_BASE}/groups`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to create group');
  }
  return res.json();
}

export async function updateGroup(id, data) {
  const res = await authFetch(`${API_BASE}/groups/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to update group');
  }
  return res.json();
}

export async function deleteGroup(id) {
  const res = await authFetch(`${API_BASE}/groups/${id}`, { method: 'DELETE' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to delete group');
  }
}

export async function addGroupMember(groupId, memberId) {
  const res = await authFetch(`${API_BASE}/groups/${groupId}/members`, {
    method: 'POST',
    body: JSON.stringify({ member_id: memberId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to add member to group');
  }
  return res.json();
}

export async function removeGroupMember(groupId, memberId) {
  const res = await authFetch(`${API_BASE}/groups/${groupId}/members/${memberId}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to remove member from group');
  }
  return res.json();
}

export async function updateGroupMembers(groupId, memberIds) {
  const res = await authFetch(`${API_BASE}/groups/${groupId}/members`, {
    method: 'PUT',
    body: JSON.stringify({ member_ids: memberIds }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to update group members');
  }
  return res.json();
}

function authFetch(url, options = {}) {
  const token = getToken();
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });
}

export async function getDashboardStats() {
  const res = await authFetch(`${API_BASE}/dashboard/stats`);
  if (!res.ok) throw new Error('Failed to fetch dashboard stats');
  return res.json();
}

export async function getMembers({ search, page, pageSize, sortBy, sortOrder, gender, type, isCompleted, staleOnly, excludedOnly } = {}) {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (page) params.set('page', page);
  if (pageSize) params.set('pageSize', pageSize);
  if (sortBy) params.set('sortBy', sortBy);
  if (sortOrder) params.set('sortOrder', sortOrder);
  if (gender) params.set('gender', gender);
  if (type) params.set('type', type);
  if (isCompleted !== undefined) params.set('is_completed', isCompleted);
  if (staleOnly) params.set('stale_only', '1');
  if (excludedOnly) params.set('excluded_only', '1');
  const res = await authFetch(`${API_BASE}/members?${params}`);
  if (!res.ok) throw new Error('Failed to fetch members');
  return res.json();
}

export async function getMember(id) {
  const res = await authFetch(`${API_BASE}/members/${id}`);
  if (!res.ok) throw new Error('Failed to fetch member');
  return res.json();
}

export async function updateMember(id, data) {
  const res = await authFetch(`${API_BASE}/members/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to update member');
  }
  return res.json();
}

export async function sendMemberNotifications(memberId) {
  const res = await authFetch(`${API_BASE}/members/${memberId}/notifications`, {
    method: 'POST',
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to send notifications');
  return data;
}

export async function getMinistries() {
  const res = await authFetch(`${API_BASE}/ministries`);
  if (!res.ok) throw new Error('Failed to fetch ministries');
  return res.json();
}

export async function createMinistry(data) {
  const payload = typeof data === 'string' ? { name: data } : data;
  const res = await authFetch(`${API_BASE}/ministries`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Failed to create ministry');
  }
  return res.json();
}

export async function updateMinistry(id, data) {
  const payload = typeof data === 'string' ? { name: data } : data;
  const res = await authFetch(`${API_BASE}/ministries/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Failed to update ministry');
  return res.json();
}

export async function deleteMinistry(id) {
  const res = await authFetch(`${API_BASE}/ministries/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.message || 'Failed to delete ministry');
  }
}

export async function createLeader(data) {
  const res = await authFetch(`${API_BASE}/leaders`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.message || 'Failed to create leader');
  }
  return res.json();
}

export async function deleteLeader(id) {
  const res = await authFetch(`${API_BASE}/leaders/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.message || 'Failed to delete leader');
  }
}

export async function updateMinistryAreasDisplayOrder(ministryId, areas) {
  const res = await authFetch(`${API_BASE}/ministries/${ministryId}/ministry-areas/display-order`, {
    method: 'PUT',
    body: JSON.stringify({ areas }),
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.message || 'Failed to update display order');
  }
  return res.json();
}

export async function updateMinistriesDisplayOrder(ministries) {
  const res = await authFetch(`${API_BASE}/ministries/display-order`, {
    method: 'POST',
    body: JSON.stringify({ ministries }),
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.message || 'Failed to update ministries display order');
  }
  return res.json();
}

export async function getMinistryAreas(ministryId = null, { sortBy, sortOrder, search } = {}) {
  const params = new URLSearchParams();
  if (ministryId) params.set('ministry_id', ministryId);
  if (sortBy) params.set('sortBy', sortBy);
  if (sortOrder) params.set('sortOrder', sortOrder);
  if (search) params.set('search', search);
  const res = await authFetch(`${API_BASE}/ministry-areas?${params}`);
  if (!res.ok) throw new Error('Failed to fetch ministry areas');
  return res.json();
}

export async function getMinistryMinistryAreas(ministryId, { sortBy, sortOrder, search } = {}) {
  const params = new URLSearchParams();
  if (sortBy) params.set('sortBy', sortBy);
  if (sortOrder) params.set('sortOrder', sortOrder);
  if (search) params.set('search', search);
  const qs = params.toString();
  const url = `${API_BASE}/ministries/${ministryId}/ministry-areas${qs ? `?${qs}` : ''}`;
  const res = await authFetch(url);
  if (!res.ok) throw new Error('Failed to fetch ministry areas');
  return res.json();
}

export async function getMinistryAreasMale() {
  const res = await authFetch(`${API_BASE}/ministry-areas/male`);
  if (!res.ok) throw new Error('Failed to fetch ministry areas');
  return res.json();
}

export async function getMinistryAreasFemale() {
  const res = await authFetch(`${API_BASE}/ministry-areas/female`);
  if (!res.ok) throw new Error('Failed to fetch ministry areas');
  return res.json();
}

export async function createMinistryArea(ministryId, data) {
  const res = await authFetch(`${API_BASE}/ministries/${ministryId}/ministry-areas`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.message || 'Failed to create ministry area');
  }
  return res.json();
}

export async function updateMinistryArea(id, data) {
  const res = await authFetch(`${API_BASE}/ministry-areas/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to update ministry area');
  return res.json();
}

export async function deleteMinistryArea(id) {
  const res = await authFetch(`${API_BASE}/ministry-areas/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error('Failed to delete ministry area');
}

export async function getInvolvements({ ministryId, ministryAreaId, search, page, pageSize, sortBy, sortOrder } = {}) {
  const params = new URLSearchParams();
  if (ministryId) params.set('ministry_id', ministryId);
  if (ministryAreaId) params.set('ministry_area_id', ministryAreaId);
  if (search) params.set('search', search);
  if (page) params.set('page', page);
  if (pageSize) params.set('pageSize', pageSize);
  if (sortBy) params.set('sortBy', sortBy);
  if (sortOrder) params.set('sortOrder', sortOrder);
  const res = await authFetch(`${API_BASE}/preferences?${params}`);
  if (!res.ok) throw new Error('Failed to fetch involvements');
  return res.json();
}

export async function getMinistryInvolvements(ministryId, { ministryAreaId, search, page, pageSize, sortBy, sortOrder } = {}) {
  const params = new URLSearchParams();
  if (ministryAreaId) params.set('ministry_area_id', ministryAreaId);
  if (search) params.set('search', search);
  if (page) params.set('page', page);
  if (pageSize) params.set('pageSize', pageSize);
  if (sortBy) params.set('sortBy', sortBy);
  if (sortOrder) params.set('sortOrder', sortOrder);
  const qs = params.toString();
  const res = await authFetch(`${API_BASE}/ministries/${ministryId}/preferences${qs ? `?${qs}` : ''}`);
  if (!res.ok) throw new Error('Failed to fetch involvements');
  return res.json();
}

export async function getMinistryAreaInvolvements(ministryId, ministryAreaId, { search, page, pageSize, sortBy, sortOrder } = {}) {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (page) params.set('page', page);
  if (pageSize) params.set('pageSize', pageSize);
  if (sortBy) params.set('sortBy', sortBy);
  if (sortOrder) params.set('sortOrder', sortOrder);
  const qs = params.toString();
  const res = await authFetch(`${API_BASE}/ministries/${ministryId}/ministry-areas/${ministryAreaId}/preferences${qs ? `?${qs}` : ''}`);
  if (!res.ok) throw new Error('Failed to fetch involvements');
  return res.json();
}

export async function deleteInvolvement(id) {
  const res = await authFetch(`${API_BASE}/preferences/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Failed to delete involvement');
}

export async function updatePreferenceIsInRotation(preferenceId, isInRotation) {
  const res = await authFetch(`${API_BASE}/preferences/${preferenceId}`, {
    method: 'PATCH',
    body: JSON.stringify({ is_in_rotation: isInRotation }),
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.message || 'Failed to update preference');
  }
  return res.json();
}

export async function exportInvolvementsCsv(ministryId, ministryAreaId = null) {
  const token = getToken();
  const url = ministryAreaId
    ? `${API_BASE}/ministries/${ministryId}/ministry-areas/${ministryAreaId}/preferences/csv`
    : `${API_BASE}/ministries/${ministryId}/preferences/csv`;
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('Failed to export CSV');
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition');
  let filename = 'preferences.csv';
  if (disposition) {
    const match = disposition.match(/filename="?([^";\n]+)"?/);
    if (match) filename = match[1];
  }
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

export async function getMemberInvolvements(memberId, params = {}) {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', params.page);
  if (params.pageSize) searchParams.set('pageSize', params.pageSize);
  if (params.search) searchParams.set('search', params.search);
  if (params.sortBy) searchParams.set('sortBy', params.sortBy);
  if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);
  const qs = searchParams.toString();
  const res = await authFetch(`${API_BASE}/members/${memberId}/preferences${qs ? `?${qs}` : ''}`);
  if (!res.ok) throw new Error('Failed to fetch involvements');
  return res.json();
}

export async function addMemberMinistryArea(memberId, ministryAreaId) {
  const res = await authFetch(`${API_BASE}/members/${memberId}/ministry-areas/${ministryAreaId}`, {
    method: 'POST',
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.message || 'Failed to add ministry area');
  }
  return res.json();
}

export async function removeMemberMinistryArea(memberId, ministryAreaId) {
  const res = await authFetch(`${API_BASE}/members/${memberId}/ministry-areas/${ministryAreaId}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.message || 'Failed to remove ministry area');
  }
}

export async function updateMemberInvolvements(memberId, ministryAreas) {
  const res = await authFetch(`${API_BASE}/members/${memberId}/preferences`, {
    method: 'PUT',
    body: JSON.stringify({ ministry_areas: ministryAreas }),
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.message || d.error || 'Failed to update involvements');
  }
  return res.json();
}
